import { KARAOKE_TRACK_NUMBER } from '@src/stores/VideoCreatorStore';

import type { LowerThirdTemplate } from '@src/components/textProcessor/TextBrandingService';
import type { ElementState } from '@src/renderer/ElementState';
import type { RendererState } from '@src/renderer/RendererState';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';

const manualChangeFields: (keyof ElementState['source'])[] = ['x', 'y'];

function detectAndApplyManualChangesToTextConfig(
  videoCreator: VideoCreatorStore,
  newState: RendererState,
): boolean {
  const activeElementOld = videoCreator.getActiveElement();
  if (
    !activeElementOld ||
    activeElementOld.source.type !== 'text' ||
    activeElementOld.source.track === KARAOKE_TRACK_NUMBER
  ) {
    return false;
  }

  const activeElementNew = newState.elements.find(
    el => el.source.id === activeElementOld.source.id,
  );
  if (!activeElementNew) {
    return false;
  }

  const propertyMap = manualChangeFields.reduce(
    (acc, field) => {
      // no change
      if (activeElementNew.source[field] === activeElementOld.source[field]) {
        return acc;
      }

      acc[field] = activeElementNew.source[field];
      return acc;
    },
    {} as Partial<ElementState['source']>,
  );

  if (Object.keys(propertyMap).length) {
    const cb = () => {
      // Check if we're moving TO the template position (applying template positioning)
      const selectedTemplate =
        videoCreator.textBrandingService.selectedBrandingTemplate;
      let shouldSkipModificationDetection = false;

      if (
        selectedTemplate &&
        videoCreator.textBrandingService.hasTemplatePositioning(
          selectedTemplate as LowerThirdTemplate,
        )
      ) {
        // Check if new position matches the template position
        const newElementMatchesTemplate =
          videoCreator.textBrandingService.doesElementMatchTemplatePosition(
            activeElementNew.source,
            selectedTemplate as LowerThirdTemplate,
          );

        // If new position matches the template, we're applying template positioning
        shouldSkipModificationDetection = newElementMatchesTemplate;
      }

      // Also skip if the flag is still set
      if (videoCreator.textBrandingService.isApplyingTemplatePosition) {
        shouldSkipModificationDetection = true;
      }

      if (!shouldSkipModificationDetection) {
        videoCreator.textBrandingService.checkPropsChanged(
          'basic',
          propertyMap,
        );
      }

      // Update the text producer config with the new position
      videoCreator.textProducer.setTextConfig({
        ...videoCreator.textProducer.getTextConfig(),
        ...propertyMap,
      });
    };

    if (videoCreator.rootStore.videoSourceStore.hasDebouncedSourceRefresh) {
      videoCreator.rootStore.videoSourceStore.onNextSourceRefresh(cb);
    } else {
      cb();
    }
  }

  return false;
}

detectAndApplyManualChangesToTextConfig.displayName =
  'detectAndApplyManualChangesToTextConfig';

export default detectAndApplyManualChangesToTextConfig;
